import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, Camera, Clock, CheckCircle2, Image as ImageIcon, Phone, PhoneCall, Plus, ScanLine, Send, Trash2, X } from "lucide-react";
import { API } from "../lib/apiClient";
import { PROVINCES, districtsOf } from "../lib/trAddress";

const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const MON = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const nextDays = () => Array.from({ length: 7 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() + i);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { label: i === 0 ? "Bugün" : i === 1 ? "Yarın" : DAYS[d.getDay()], sub: `${d.getDate()} ${MON[d.getMonth()]}`, iso };
});
const low = (s) => (s || "").replace(/İ/g, "i").replace(/I/g, "i").toLowerCase().replace(/ı/g, "i");
const IMM = ["500.000 TL", "1.000.000 TL", "2.000.000 TL", "3.000.000 TL", "4.000.000 TL", "5.000.000 TL", "7.000.000 TL", "10.000.000 TL", "Sınırsız"];
const JOBS = ["Özel sektör çalışanı", "Kamu çalışanı (memur)", "Öğretmen", "Akademisyen", "Doktor", "Diş hekimi", "Eczacı", "Hemşire / sağlık personeli", "Mühendis", "Mimar", "Avukat", "Hakim / savcı", "Mali müşavir / muhasebeci", "Bankacı / finans", "Asker", "Polis", "Esnaf / tüccar", "İşveren / şirket sahibi", "Serbest meslek", "Şoför / nakliyeci", "Çiftçi", "Emekli", "Öğrenci", "Ev hanımı", "Çalışmıyor", "Diğer"];
const IKAME = ["Yok", "7 gün × 2 kez", "14 gün × 2 kez"];
const isImm = (b) => /imm/.test(low(b));
const isKasko = (b) => /kasko/.test(low(b));
export const quoteKind = (b) => { const x = low(b); return /trafik|kasko|imm/.test(x) ? "vehicle" : /dask|deprem/.test(x) ? "dask" : /tamamlay|tss/.test(x) ? "tss" : ""; };
const digits = (v, n) => v.replace(/\D/g, "").slice(0, n);
const emptyMember = () => ({ relation: "", tcKimlik: "", birthDate: "", height: "", weight: "" });

function missingOf(f) {
  const m = [];
  if (!f.customerName.trim()) m.push("Ad Soyad");
  if (f.customerPhone.replace(/\D/g, "").length < 10) m.push("Telefon");
  if (!/^\d{11}$/.test(f.tcKimlik)) m.push("T.C. (11 hane)");
  if (!f.birthDate) m.push("Doğum Tarihi");
  const k = quoteKind(f.branch);
  if (k === "vehicle") {
    if (!f.plate.trim()) m.push("Plaka");
    if (!/^[A-ZÇĞİÖŞÜ]{2}\d{6}$/.test(f.ruhsatSeriNo)) m.push("Ruhsat Seri No");
    if ((isImm(f.branch) || isKasko(f.branch)) && !f.immLimit) m.push("İMM limiti");
    if (isKasko(f.branch) && !f.ikame) m.push("İkame araç");
    if (isKasko(f.branch) && !f.profession) m.push("Meslek");
  }
  if (k === "dask" && !(/^\d{10}$/.test(f.uavt) || (f.city && f.district && f.address.trim()))) m.push("Adres bilgisi veya UAVT kodu");
  if (k === "tss") {
    if (!f.tssType) m.push("Bireysel / Aile");
    if (!f.height || !f.weight) m.push("Boy / Kilo");
    if (f.tssType === "Aile") {
      if (!f.members.length) m.push("Aile bireyi");
      f.members.forEach((x, i) => { if (!(/^\d{11}$/.test(x.tcKimlik) && x.birthDate && x.height && x.weight)) m.push(`${i + 2}. kişi bilgileri`); });
    }
  }
  if (f.callRequest === null) m.push("Arama tercihi");
  if (f.callRequest && !f.callDay) m.push("Arama günü");
  if (f.callRequest && !f.callSlot) m.push("Arama saati");
  return m;
}

function F({ label, req, opt, children }) {
  return <label className="pq-field"><span>{label}{req && " *"}{opt && <em> (isteğe bağlı)</em>}</span>{children}</label>;
}

function BodyFields({ v, set, prefix }) {
  return (
    <div className="pq-grid">
      <F label="Boy (cm)" req><input inputMode="numeric" value={v.height} onChange={(e) => set("height", digits(e.target.value, 3))} placeholder="175" data-testid={`${prefix}-height`} /></F>
      <F label="Kilo (kg)" req><input inputMode="numeric" value={v.weight} onChange={(e) => set("weight", digits(e.target.value, 3))} placeholder="70" data-testid={`${prefix}-weight`} /></F>
    </div>
  );
}

function TssBlock({ form, setForm }) {
  const setM = (i, k, val) => setForm((f) => ({ ...f, members: f.members.map((m, j) => (j === i ? { ...m, [k]: val } : m)) }));
  return (
    <div className="pq-block" data-testid="public-tss-block">
      <div className="pq-seg">
        {["Bireysel", "Aile"].map((t) => (
          <button key={t} type="button" className={form.tssType === t ? "on" : ""} data-testid={`public-tss-${t}`}
            onClick={() => setForm((f) => ({ ...f, tssType: t, members: t === "Aile" ? (f.members.length ? f.members : [emptyMember()]) : [] }))}>{t}</button>
        ))}
      </div>
      {form.tssType && (<>
        <small className="pq-sub">1. Sigortalı (siz)</small>
        <BodyFields v={form} set={(k, val) => setForm((f) => ({ ...f, [k]: val }))} prefix="public-p1" />
      </>)}
      {form.tssType === "Aile" && form.members.map((m, i) => (
        <div key={i} className="pq-member" data-testid={`public-member-${i}`}>
          <div className="pq-member-head"><small className="pq-sub">{i + 2}. Sigortalı</small>
            <button type="button" className="pq-icon" onClick={() => setForm((f) => ({ ...f, members: f.members.filter((_, j) => j !== i) }))} data-testid={`public-member-remove-${i}`}><Trash2 size={14} /></button></div>
          <div className="pq-grid">
            <F label="Yakınlık" opt><select value={m.relation} onChange={(e) => setM(i, "relation", e.target.value)} data-testid={`public-member-${i}-relation`}>
              <option value="">Seçin</option>{["Eş", "Çocuk", "Anne", "Baba", "Diğer"].map((r) => <option key={r}>{r}</option>)}</select></F>
            <F label="T.C. Kimlik No" req><input inputMode="numeric" value={m.tcKimlik} onChange={(e) => setM(i, "tcKimlik", digits(e.target.value, 11))} placeholder="11 haneli" data-testid={`public-member-${i}-tc`} /></F>
            <F label="Doğum Tarihi" req><input type="date" value={m.birthDate} onChange={(e) => setM(i, "birthDate", e.target.value)} data-testid={`public-member-${i}-birth`} /></F>
          </div>
          <BodyFields v={m} set={(k, val) => setM(i, k, val)} prefix={`public-member-${i}`} />
        </div>
      ))}
      {form.tssType === "Aile" && <button type="button" className="pq-add" onClick={() => setForm((f) => ({ ...f, members: [...f.members, emptyMember()] }))} data-testid="public-member-add"><Plus size={14} /> Kişi ekle</button>}
    </div>
  );
}

function DaskBlock({ form, set }) {
  return (
    <div className="pq-block" data-testid="public-dask-block">
      <small className="pq-hint">Adres bilgilerini veya UAVT (adres) kodunu girin — ikisinden biri zorunludur.</small>
      <F label="UAVT Kodu"><input inputMode="numeric" value={form.uavt} onChange={(e) => set("uavt", digits(e.target.value, 10))} placeholder="10 haneli adres kodu" data-testid="public-uavt" /></F>
      <div className="pq-or"><span>veya</span></div>
      <div className="pq-grid">
        <F label="İl"><select value={form.city} onChange={(e) => { set("city", e.target.value); set("district", ""); }} data-testid="public-city"><option value="">İl seçin</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></F>
        <F label="İlçe"><select value={form.district} disabled={!form.city} onChange={(e) => set("district", e.target.value)} data-testid="public-district"><option value="">{form.city ? "İlçe seçin" : "Önce il seçin"}</option>{districtsOf(form.city).map((d) => <option key={d}>{d}</option>)}</select></F>
      </div>
      <F label="Açık adres"><textarea rows="2" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Mahalle, cadde/sokak, bina no, daire no" data-testid="public-address" /></F>
    </div>
  );
}

function VehicleBlock({ form, set, slug }) {
  const camRef = useRef(null); const galRef = useRef(null);
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setMsg("Lütfen bir fotoğraf seçin."); return; }
    if (file.size > 8 * 1024 * 1024) { setMsg("Dosya 8 MB'tan büyük olamaz."); return; }
    setBusy(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/public/ocr-ruhsat/${slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const filled = [];
      if (data.plate && !form.plate) { set("plate", data.plate); filled.push("plaka"); }
      if (data.tcKimlik && !form.tcKimlik) { set("tcKimlik", data.tcKimlik); filled.push("TC"); }
      if (data.ruhsatSeriNo && !form.ruhsatSeriNo) { set("ruhsatSeriNo", data.ruhsatSeriNo); filled.push("ruhsat seri no"); }
      setMsg(filled.length ? `✓ ${filled.join(", ")} otomatik dolduruldu.` : "Fotoğraftan bilgi okunamadı, elle doldurabilirsiniz.");
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Okunamadı. Elle doldurabilirsiniz."); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="pq-ocr" data-testid="public-ocr-dropzone">
        <ScanLine size={18} />
        <div><strong>Ruhsat fotoğrafı ile otomatik doldur</strong><small>Plaka, TC ve ruhsat seri no fotoğraftan okunur.</small></div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={handle} data-testid="public-ocr-camera-file" />
        <input ref={galRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handle} data-testid="public-ocr-file" />
        <button type="button" className="pq-cam" disabled={busy} onClick={() => camRef.current?.click()} data-testid="public-ocr-camera"><Camera size={14} /> {busy ? "Okunuyor…" : "Kamera"}</button>
        <button type="button" className="pq-gal" disabled={busy} onClick={() => galRef.current?.click()} data-testid="public-ocr-button"><ImageIcon size={14} /> Galeri</button>
      </div>
      {msg && <small className={msg.startsWith("✓") ? "pq-ok" : "pq-warn"} data-testid="public-ocr-msg">{msg}</small>}
      <div className="pq-grid">
        <F label="Plaka" req><input value={form.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="34 ABC 123" data-testid="public-plate" /></F>
        <F label="Ruhsat Seri No" req><input value={form.ruhsatSeriNo} onChange={(e) => set("ruhsatSeriNo", e.target.value.toUpperCase().replace(/[^A-ZÇĞİÖŞÜ0-9]/g, "").slice(0, 8))} placeholder="AB 123456" data-testid="public-ruhsat" /></F>
      </div>
    </>
  );
}

function CallBlock({ form, set }) {
  const days = useMemo(nextDays, []);
  return (
    <div className="pq-call">
      <div className="pq-call-q"><PhoneCall size={15} /> <span>Sizi aramamızı ister misiniz? *</span></div>
      <div className="pq-seg small">
        <button type="button" className={form.callRequest === true ? "on" : ""} onClick={() => set("callRequest", true)} data-testid="public-call-yes">Evet</button>
        <button type="button" className={form.callRequest === false ? "on" : ""} onClick={() => { set("callRequest", false); set("callDay", ""); set("callDate", ""); set("callSlot", ""); }} data-testid="public-call-no">Hayır</button>
      </div>
      {form.callRequest && (<>
        <div className="pq-call-q"><Phone size={14} /> <span>Sizi ne zaman arayalım?</span></div>
        <div className="pq-days" data-testid="public-call-days">
          {days.map((d) => { const val = `${d.label} (${d.sub})`; return (
            <button key={d.sub} type="button" className={form.callDay === val ? "on" : ""} onClick={() => { set("callDay", val); set("callDate", d.iso); }} data-testid={`public-call-day-${d.sub.replace(" ", "-")}`}><b>{d.label}</b><small>{d.sub}</small></button>
          ); })}
        </div>
        <div className="pq-call-q"><Clock size={14} /> <span>Hangi saat aralığında?</span></div>
        <div className="pq-days slots" data-testid="public-call-slots">
          {SLOTS.map(([k, r]) => <button key={k} type="button" className={form.callSlot === k ? "on" : ""} onClick={() => set("callSlot", k)} data-testid={`public-call-slot-${k.replace(" ", "-")}`}><b>{k}</b><small>{r}</small></button>)}
        </div>
      </>)}
    </div>
  );
}

const SLOTS = [["Sabah", "09:00–12:00"], ["Öğleden sonra", "12:00–17:00"], ["Akşam", "17:00–20:00"]];

const initial = { customerName: "", customerPhone: "", customerEmail: "", tcKimlik: "", birthDate: "", branch: "", plate: "", ruhsatSeriNo: "", city: "", district: "", address: "", uavt: "", tssType: "", height: "", weight: "", members: [], callRequest: null, callDay: "", callDate: "", callSlot: "", immLimit: "", profession: "", ikame: "", note: "" };

export function PublicQuoteForm({ slug }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/quote/${slug}`).then(({ data }) => { setInfo(data); setForm((f) => ({ ...f, branch: f.branch || data.branches?.[0]?.name || "" })); })
      .catch(() => setErr("Geçersiz veya süresi dolmuş teklif linki."));
  }, [slug]);
  const visitRef = useRef(sessionStorage.getItem(`qrvisit-${slug}`) || "");
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("src") !== "qr") return;
    let id = localStorage.getItem("qr-device-id");
    if (!id) { id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`; localStorage.setItem("qr-device-id", id); }
    if (visitRef.current) return;
    axios.post(`${API}/public/quote/${slug}/scan`, { deviceId: id }).then(({ data }) => {
      if (data.visitId) { visitRef.current = data.visitId; sessionStorage.setItem(`qrvisit-${slug}`, data.visitId); }
    }).catch(() => {});
  }, [slug]);
  useEffect(() => {
    if (!visitRef.current || (!form.customerName && !form.customerPhone)) return undefined;
    const t = setTimeout(() => axios.patch(`${API}/public/qr-visit/${visitRef.current}`, { name: form.customerName, phone: form.customerPhone }).catch(() => {}), 1200);
    return () => clearTimeout(t);
  }, [form.customerName, form.customerPhone]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const missing = missingOf(form);
  const kind = quoteKind(form.branch);
  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) return;
    setBusy(true); setErr("");
    try { await axios.post(`${API}/public/quote/${slug}`, { ...form, callRequest: !!form.callRequest, channel: new URLSearchParams(window.location.search).get("src") === "qr" ? "qr" : "link" }); setDone(true);
      if (visitRef.current) { axios.post(`${API}/public/qr-visit/${visitRef.current}/submitted`).catch(() => {}); sessionStorage.removeItem(`qrvisit-${slug}`); visitRef.current = ""; } }
    catch (e2) { setErr(e2?.response?.data?.detail || "Gönderim başarısız."); } finally { setBusy(false); }
  };
  if (err && !info) return <div className="pq-center" data-testid="public-quote-error"><AlertTriangle size={32} color="#dc2626" /><h2>Teklif alınamıyor</h2><p>{err}</p></div>;
  if (!info) return <div className="pq-center" data-testid="public-quote-loading">Yükleniyor…</div>;
  if (done) return <div className="pq-center" data-testid="public-quote-success"><CheckCircle2 size={44} color="#059669" /><h2>Teklif talebiniz alındı</h2><p>{info.agentName} en kısa sürede sizinle iletişime geçecek.</p></div>;
  return (
    <div className="pq-page" data-testid="public-quote-form">
      <div className="pq-wrap">
        <header className="pq-agent" data-testid="public-agent">
          {info.logoUrl ? <img src={info.logoUrl} alt="" data-testid="public-agent-logo" /> : <span className="pq-mono">{(info.agentName || "S").slice(0, 1)}</span>}
          <div><strong>{info.agentName}</strong>{info.agentPhone && <a href={`tel:${info.agentPhone}`}><Phone size={11} /> {info.agentPhone}</a>}</div>
        </header>
        <h1>Teklif Talebi</h1>
        <p className="pq-lead">Bilgilerinizi bırakın, size en uygun sigorta tekliflerini hazırlayalım.</p>
        <form onSubmit={submit}>
          <div className="pq-card">
            <F label="Ad Soyad" req><input value={form.customerName} onChange={(e) => set("customerName", e.target.value)} data-testid="public-name" /></F>
            <div className="pq-grid">
              <F label="Telefon" req><input type="tel" inputMode="tel" value={form.customerPhone} onChange={(e) => set("customerPhone", e.target.value)} placeholder="05xx xxx xx xx" data-testid="public-phone" /></F>
              <F label="E-posta" opt><input type="email" value={form.customerEmail} onChange={(e) => set("customerEmail", e.target.value)} data-testid="public-email" /></F>
              <F label="T.C. Kimlik No" req><input inputMode="numeric" value={form.tcKimlik} onChange={(e) => set("tcKimlik", digits(e.target.value, 11))} placeholder="11 haneli" data-testid="public-tc" /></F>
              <F label="Doğum Tarihi" req><input type="date" value={form.birthDate} onChange={(e) => set("birthDate", e.target.value)} data-testid="public-birth" /></F>
            </div>
            <F label="Sigorta Türü" req>
              <select value={form.branch} onChange={(e) => set("branch", e.target.value)} data-testid="public-branch">
                {(info.branches || []).map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
            </F>
            {kind === "vehicle" && <VehicleBlock form={form} set={set} slug={slug} />}
            {(isKasko(form.branch) || isImm(form.branch)) && (
              <div className="pq-grid" data-testid="public-vehicle-extras">
                <F label="İMM limiti" req><select value={form.immLimit} onChange={(e) => set("immLimit", e.target.value)} data-testid="public-imm">
                  <option value="">Seçin</option>{IMM.map((x) => <option key={x}>{x}</option>)}</select></F>
                {isKasko(form.branch) && <F label="İkame araç" req><select value={form.ikame} onChange={(e) => set("ikame", e.target.value)} data-testid="public-ikame">
                  <option value="">Seçin</option>{IKAME.map((x) => <option key={x}>{x}</option>)}</select></F>}
                {isKasko(form.branch) && <F label="Meslek" req><select value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="public-profession">
                  <option value="">Seçin</option>{JOBS.map((x) => <option key={x}>{x}</option>)}</select></F>}
              </div>
            )}
            {kind === "dask" && <DaskBlock form={form} set={set} />}
            {kind === "tss" && <TssBlock form={form} setForm={setForm} />}
            <CallBlock form={form} set={set} />
            <F label="Mesajınız" opt><textarea rows="3" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Örn: Trafik + Kasko fiyatı istiyorum" data-testid="public-note" /></F>
          </div>
          {missing.length > 0 && <p className="pq-missing" data-testid="public-missing">Eksik: {missing.join(", ")}</p>}
          {err && <p className="pq-missing" data-testid="public-error"><X size={13} /> {err}</p>}
          <button type="submit" className="pq-submit" disabled={busy || missing.length > 0} data-testid="public-submit"><Send size={16} /> {busy ? "Gönderiliyor…" : "Teklif Talebi Gönder"}</button>
          <p className="pq-foot">Bilgileriniz yalnızca teklif hazırlamak amacıyla {info.agentName} ile paylaşılır.</p>
        </form>
      </div>
    </div>
  );
}
